import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

import Breadcrumb from 'components/ui/Breadcrumb';
import DashboardSidebar from './components/DashboardSidebar';
import DashboardMetrics from './components/DashboardMetrics';
import RecentActivity from './components/RecentActivity';
import SavedJobs from './components/SavedJobs';
import ApplicationTracker from './components/ApplicationTracker';
import JobAlerts from './components/JobAlerts';
import ProfileCompletion from './components/ProfileCompletion';
import ResumeManager from './components/ResumeManager';
import TagManager from 'components/ui/TagManager';
import MobileNavigation from './components/MobileNavigation';

const JobSeekerDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState({
    name: "User",
    email: "",
    profileImage: null,
    resumeUrl: null,
    resumeName: null,
    profileCompletion: 0,
    metrics: {
      applicationsSubmitted: 0,
      interviewsScheduled: 0,
      savedJobs: 0,
      newMatches: 0
    },
    skills: [],
    roles: []
  });

  const fetchDashboardData = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      // Fetch profiles
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      // Fetch application count (Jobs Submitted)
      const { count: appCount } = await supabase
        .from('job_applications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      // Fetch interviews count
      const { count: interviewCount } = await supabase
        .from('job_applications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('status', 'interviewing');

      // Fetch saved jobs count
      const { count: savedCount } = await supabase
        .from('saved_jobs')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      setUserData({
        name: profile?.full_name || (profile?.first_name ? `${profile.first_name} ${profile.last_name || ''}`.trim() : user.email.split('@')[0]),
        email: user.email,
        profileImage: profile?.avatar_url,
        profileImage: profile?.avatar_url,
        resumeUrl: profile?.resume_url,
        resumeName: profile?.resume_name,
        profileCompletion: profile?.first_name ? 70 : 30, // Updated check
        metrics: {
          applicationsSubmitted: appCount || 0,
          interviewsScheduled: interviewCount || 0,
          savedJobs: savedCount || 0,
          newMatches: 0 // Not implemented yet
        },
        skills: profile?.skills || [],
        roles: profile?.preferred_roles || []
      });
    } catch (err) {
      console.error('Error fetching dashboard data:', err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [user]);

  // Generic handler to update array columns in profile
  const updateProfileArray = async (column, newArray) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ [column]: newArray })
        .eq('id', user.id);

      if (error) throw error;

      // Update local state
      setUserData(prev => ({
        ...prev,
        [column === 'preferred_roles' ? 'roles' : column]: newArray
      }));
    } catch (err) {
      console.error(`Error updating ${column}:`, err);
      alert(`Failed to save changes: ${err.message}`);
    }
  };

  const handleAddSkill = (skill) => {
    const newSkills = [...userData.skills, skill];
    updateProfileArray('skills', newSkills);
  };

  const handleRemoveSkill = (skillToRemove) => {
    const newSkills = userData.skills.filter(s => s !== skillToRemove);
    updateProfileArray('skills', newSkills);
  };

  const handleAddRole = (role) => {
    const newRoles = [...userData.roles, role];
    updateProfileArray('preferred_roles', newRoles);
  };

  const handleRemoveRole = (roleToRemove) => {
    const newRoles = userData.roles.filter(r => r !== roleToRemove);
    updateProfileArray('preferred_roles', newRoles);
  };

  const handleUpdateName = async (newName) => {
    try {
      // Split single name string into first and last name
      const nameParts = newName.trim().split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ');

      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: firstName,
          last_name: lastName
        })
        .eq('id', user.id);

      if (error) throw error;

      setUserData(prev => ({
        ...prev,
        name: newName
      }));
    } catch (err) {
      console.error('Error updating name:', err);
      alert('Failed to update name');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <h2 className="text-2xl font-bold mb-4">Please log in to view your dashboard</h2>
        <Link to="/job-seeker-registration-login" className="btn-primary">Go to Login</Link>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <>
            <DashboardMetrics metrics={userData.metrics} />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
              <div className="lg:col-span-2">
                <RecentActivity />
              </div>
              <div className="lg:col-span-1">
                <ProfileCompletion
                  completion={userData.profileCompletion}
                  onNavigate={setActiveTab}
                />
              </div>
            </div>
          </>
        );
      case 'applications':
        return <ApplicationTracker onRefresh={fetchDashboardData} />;
      case 'saved':
        return <SavedJobs onRefresh={fetchDashboardData} />;
      case 'resume':
        return (
          <ResumeManager
            resumeUrl={userData.resumeUrl}
            resumeName={userData.resumeName}
            onUpdate={(url, name) => setUserData(prev => ({ ...prev, resumeUrl: url, resumeName: name }))}
          />
        );
      case 'skills':
        return (
          <TagManager
            title="My Professional Skills"
            tags={userData.skills}
            onAdd={handleAddSkill}
            onRemove={handleRemoveSkill}
            placeholder="e.g. React, Python..."
          />
        );
      case 'roles':
        return (
          <TagManager
            title="Target Job Roles"
            tags={userData.roles}
            onAdd={handleAddRole}
            onRemove={handleRemoveRole}
            placeholder="e.g. Frontend Developer..."
          />
        );
      default:
        return <div>Content not available</div>;
    }
  };

  return (
    <div className="bg-surface min-h-screen pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Breadcrumb />

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Desktop Sidebar */}
          <div className="hidden lg:block lg:w-64 flex-shrink-0">
            <DashboardSidebar
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              userData={userData}
            />
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="bg-background rounded-lg shadow-sm border border-border p-6 mb-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-2xl font-bold text-text-primary">
                    {activeTab === 'overview' ? 'Dashboard' :
                      activeTab === 'applications' ? 'Applications' :
                        activeTab === 'resume' ? 'My Resume' :
                          activeTab === 'skills' ? 'Skills' :
                            activeTab === 'roles' ? 'Roles' : 'Dashboard'}
                  </h1>
                  <p className="text-text-secondary mt-1">
                    {activeTab === 'overview' ? 'Welcome back, ' + userData.name :
                      activeTab === 'applications' ? 'Track your job applications' :
                        activeTab === 'resume' ? 'Manage your CV/Resume' :
                          activeTab === 'skills' ? 'Manage your professional skills' :
                            activeTab === 'roles' ? 'Manage your preferred job roles' : ''}
                  </p>
                </div>

                <div className="flex space-x-3">

                  <Link to="/job-search-browse" className="btn-primary flex items-center space-x-2">
                    <Icon name="Search" size={16} />
                    <span className="hidden sm:inline">Find Jobs</span>
                  </Link>
                </div>
              </div>

              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-background border-t border-border z-10">
        <MobileNavigation activeTab={activeTab} setActiveTab={setActiveTab} />
      </div>
    </div>
  );
};

export default JobSeekerDashboard;